// src/app/store/slices/authSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authApi from "../../../features/auth/services/authApi";
import {
  getUserFromStorage,
  getTokenFromStorage,
  setAuthStorage,
  clearAuthStorage,
} from "../../../shared/utils/storage";

/* ================================
   ASYNC THUNKS
================================ */

// Login
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Register
export const register = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.register(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Forgot Password
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authApi.forgotPassword(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Reset Password
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await authApi.resetPassword(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Logout
export const logout = createAsyncThunk("auth/logout", async () => {
  await authApi.logout();
});

/* ================================
   INITIAL STATE (Safe Storage)
================================ */

const storedUser = getUserFromStorage();
const storedToken = getTokenFromStorage();

const initialState = {
  user: storedUser,
  token: storedToken || null,
  isLoading: false,
  error: null,
  isAuthenticated: !!storedToken,
  message: null,
};

/* ================================
   SLICE
================================ */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ========= LOGIN ========= */
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        const { user, access_token } = action.payload.data;
        state.user = user;
        state.token = access_token;

        setAuthStorage(user, access_token);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Login failed";
      })

      /* ========= REGISTER ========= */
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message =
          action.payload.message || "Registration successful. Please login.";
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Registration failed";
      })

      /* ========= FORGOT PASSWORD ========= */
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message =
          action.payload.message ||
          "Password reset link sent to your email.";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || "Failed to send reset link";
      })

      /* ========= RESET PASSWORD ========= */
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message =
          action.payload.message ||
          "Password reset successful. Please login.";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || "Failed to reset password";
      })

      /* ========= LOGOUT ========= */
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;

        clearAuthStorage();
      });
  },
});

export const { clearError, clearMessage, setUser } = authSlice.actions;
export default authSlice.reducer;